<template>
  <cs-preloader-overlay :loading="loading">
    <div
      v-if="!loading"
      :class="[
        $style.container,
        {
          [$style.expire]: isExpiredStatus,
          [$style.success]: isSuccessStatus,
        }
      ]"
    >
      <payment-header
        :title="$t('crypto.payment.header.title', { currency: currency.code })"
        :language="getLanguage"
        has-redirect
        @redirect-back="$router.go(-1)"
      />
      <div :class="$style.content">
        <crypto-payment-information
          v-m-b="30"
          :transaction="transaction"
        />
        <empty-crypto-payment-list
          v-if="isEmptyList"
          v-position="'relative'"
          v-z-index="1000"
        />
        <crypto-payment-list
          v-else
          v-position="'relative'"
          v-z-index="1000"
          :transaction="transaction"
          :transactions-groups="transactions"
        />

        <simple-button
          v-if="isExpiredStatus"
          v-m-t="30"
          v-position="'relative'"
          v-z-index="1000"
          variant="secondary"
          @click="handleRedirect"
        >
          {{ $t('crypto.payment.expire.submit') }}
        </simple-button>
      </div>
      <div :class="$style.footer">
        <base-footer />
      </div>
    </div>
  </cs-preloader-overlay>
</template>

<script>
import { mapGetters } from 'vuex';

import { formatDateToCustomDate } from 'lib/utils/date';

import HTTPService from 'services/Http';

import BaseFooter from 'molecules/base/BaseFooter';
import SimpleButton from 'molecules/common/SimpleButton';
import PaymentHeader from 'organisms/payment/PaymentHeader';

import EmptyCryptoPaymentList from 'organisms/crypto/EmptyCryptoPaymentList';
import CryptoPaymentInformation from 'organisms/crypto/CryptoPaymentInformation';
import CryptoPaymentList from 'organisms/crypto/CryptoPaymentList';

export default {
  name: 'HistoryTransactionList',
  components: {
    BaseFooter,
    SimpleButton,
    PaymentHeader,
    EmptyCryptoPaymentList,
    CryptoPaymentInformation,
    CryptoPaymentList,
  },
  created() {
    this.initializeData();
  },
  data() {
    return {
      loading: false,
      transaction: {},
    };
  },
  computed: {
    paymentId() {
      const { params } = this.$route;

      if (!this.$validate.isObjectWithFields(params, ['id'])) {
        return null;
      }

      if (this.$validate.isEmptyString(params.id)) {
        return null;
      }

      return params.id;
    },
    projectCode() {
      const { query } = this.$route;

      if (!this.$validate.isObjectWithFields(query, ['projectCode'])) {
        return null;
      }

      if (this.$validate.isEmptyString(query.projectCode)) {
        return null;
      }

      return query.projectCode;
    },
    isInvalid() {
      return !this.paymentId || !this.projectCode;
    },
    currency() {
      return this.transaction.currency;
    },
    transactions() {
      const result = {};
      const { transactions } = this.transaction;

      if (this.$validate.isNull(transactions)) {
        return result;
      }

      transactions
        .forEach(transaction => {
          const key = formatDateToCustomDate(transaction.createdAt, 'DD.MM.YYYY');

          result[key] = result[key] || [];
          result[key].push(transaction);
        });

      return result;
    },
    isEmptyList() {
      return this.$validate.isEmptyObject(this.transactions);
    },
    isSuccessStatus() {
      return this.transaction.status === 'SUCCESS';
    },
    isExpiredStatus() {
      return this.transaction.status === 'EXPIRED';
    },
    ...mapGetters([
      'getLanguage',
      'getFailUrl',
      'getProjectUrl',
    ]),
  },
  methods: {
    initializeData() {
      if (this.isInvalid) {
        this.$router.push({ name: 'service.error' });
        return;
      }

      this.loadData();
    },
    async loadData() {
      this.loading = true;

      try {
        const { data } = await HTTPService.getCryptoTransactionsByParams({
          paymentId: this.paymentId,
          projectCode: this.projectCode,
        });

        console.log(data);

        if (!['SUCCESS', 'EXPIRED'].includes(data.status)) {
          this.$router.push({ name: 'service.error' });
          return;
        }

        this.transaction = data;
      } catch (error) {
        this.$router.push({ name: 'service.error' });
      } finally {
        this.loading = false;
      }
    },
    handleRedirect() {
      if (this.getFailUrl) {
        window.location.assign(this.getFailUrl);
        return;
      }

      window.location.assign(this.getProjectUrl);
    },
  },
};
</script>

<style lang="sass" module>
  .container
    +flexbox
    +flex-direction(column)
    min-height: 100vh
    position: relative

    &:before
      content: ''
      position: absolute
      width: 100%
      height: 100%
      top: 0
      right: 0
      background: url('@/assets/images/crypto-payment/cloud-1.svg') no-repeat center
      background-size: cover

    &:after
      content: ''
      position: absolute
      width: 100%
      height: 100%
      top: 0
      left: 0
      background: url('@/assets/images/crypto-payment/cloud-2.svg') no-repeat center
      background-size: cover

    &.expire

      &:after
        content: none

      .content
        max-width: 380px

    &.success

      +respond-to('medium')
        &:before
          +size(470px, 386px)
          top: 400px
          background: url('@/assets/images/crypto-payment/stars-1.svg') no-repeat center

        &:after
          +size(470px, 386px)
          top: 400px
          background: url('@/assets/images/crypto-payment/stars-2.svg') no-repeat center

  .content
    +flex-grow
    width: 100%
    max-width: 500px
    margin: 0 auto
    padding: 30px 10px

  .footer
    margin-bottom: 20px
    padding: 0 10px
</style>
